import type { ReviewableProduct } from '../reviewableProduct';
import type { ReviewFormData } from './reviewFormData';
import type { ReviewOption } from './reviewOptionsField/reviewOptionsField';

/**
 * Which screen the modal shows: the list of products to pick from, the
 * questions, the submit error, or the confirmation.
 */
export type RateAndReviewStatus =
  'choosing' | 'editing' | 'failed' | 'submitted';

/** Props shared by every A/B test variant of the Rate & Review modal. */
export interface RateAndReviewModalVariantProps {
  activityOptions: ReviewOption[];
  brand?: string;
  category?: string;
  feelingOptions: ReviewOption[];
  /** When `status` is `failed`: replaces the default message. */
  errorMessage?: string;
  imageUrl?: string;
  /** Bottom sheet on mobile, centered dialog otherwise. */
  isMobile: boolean;
  /** When `status` is `failed`: is it possible for re-submitting to succeed? */
  isRetryable?: boolean;
  isSubmitting?: boolean;
  /** Other products to review, suggested on the confirmation and listed when `choosing`. */
  moreToReview?: ReviewableProduct[];
  /**
   * Shows a back button while `editing` (from the first question) and when
   * `failed` without a retry.
   */
  onBack?: () => void;
  onChange: (data: ReviewFormData) => void;
  /** Also fired by the confirmation's "I'm done". */
  onRequestClose: () => void;
  /** Fired when a product from `moreToReview` is picked. */
  onReviewProduct: (product: ReviewableProduct) => void;
  onSubmit: () => void;
  open: boolean;
  productName: string;
  /** Defaults to `editing`. `submitted` uses `value.rating` for the confirmation. */
  status?: RateAndReviewStatus;
  testId?: string;
  /** Defaults to "Rate and review". */
  title?: string;
  value: ReviewFormData;
}

export const canGoBack = ({
  isRetryable = true,
  isSubmitting = false,
  status = 'editing',
}: Pick<
  RateAndReviewModalVariantProps,
  'isRetryable' | 'isSubmitting' | 'status'
>) =>
  !isSubmitting &&
  (status === 'editing' || (status === 'failed' && !isRetryable));
